import React, { useState, useMemo } from "react";
import {
  Search,
  Eye,
  Trash2,
  Calendar,
  FileText,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import {
  useGetFeedbackRequestQuery,
  useDeleteFeedbackRequestMutation,
} from "../slices/feedbackSlice";
import { useGetAcademyQuery } from "../slices/userApiSlice";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import CustomSelect from "../components/CustomSelect/CustomSelect";
import { toast } from "sonner";

const FeedbackReport = () => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: feedbackData, isLoading: isFeedbackLoading } =
    useGetFeedbackRequestQuery();
  const { data: academies } = useGetAcademyQuery();
  const [deleteFeedback] = useDeleteFeedbackRequestMutation();

  const getAcademyName = (academyId) => {
    if (!academyId || !academies) return "Unknown Academy";
    const academy = academies.find((a) => a._id === academyId);
    return academy?.name || "Unknown Academy";
  };

  const getItemAcademy = (item) => {
    if (!item || !item.academy) return null;
    if (typeof item.academy === "object") {
      return item.academy._id || item.academy.id || item.academy.academyId;
    }
    return item.academy;
  };

  const getAcademyDisplayName = (item) => {
    if (!item || !item.academy) return "Unknown Academy";

    if (typeof item.academy === "object") {
      return item.academy.name || "Unknown Academy";
    }

    const academyIdOrName = item.academy;
    if (!academies || !Array.isArray(academies)) return academyIdOrName;

    const match = academies.find((a) => {
      const id = a._id || a.id || a.academyId;
      return (
        (id && String(id) === String(academyIdOrName)) ||
        a.name === academyIdOrName
      );
    });

    return match?.name || academyIdOrName;
  };

  const resolveSubmissionDate = (item) => {
    if (!item) return null;
    const candidate =
      item?.createdAt ||
      item?.created_at ||
      item?.submittedAt ||
      item?.submitted_at ||
      item?.date ||
      item?.feedbackDate ||
      item?.timestamp ||
      item?.createdOn ||
      item?.created_on ||
      null;

    return parseTimestamp(candidate) || deriveFromObjectId(item);
  };

  const formatSubmissionTimestamp = (item) => {
    const parsed = resolveSubmissionDate(item);
    if (!parsed) return "Unknown";

    return parsed.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const parseTimestamp = (raw) => {
    if (raw == null || raw === "") return null;

    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;

    if (typeof raw === "number") {
      return new Date(raw < 10_000_000_000 ? raw * 1000 : raw);
    }

    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return null;

      if (/^-?\d+$/.test(trimmed)) {
        const num = Number(trimmed);
        return new Date(trimmed.length === 10 ? num * 1000 : num);
      }

      const normalized = trimmed.replace(/,/g, "");
      const direct = new Date(normalized);
      if (!Number.isNaN(direct.getTime())) return direct;

      const dayFirstMatch = normalized.match(
        /^([0-3]?\d)[-/]([0-3]?\d)[-/](\d{2,4})(?:\s+([0-2]?\d)(?::([0-5]?\d))?(?::([0-5]?\d))?\s*(AM|PM)?)?$/i,
      );
      if (dayFirstMatch) {
        const [, dd, mm, yyyyRaw, hhRaw, minRaw, secRaw, meridian] =
          dayFirstMatch;
        const year = Number(yyyyRaw.length === 2 ? `20${yyyyRaw}` : yyyyRaw);
        let hours = Number(hhRaw ?? "0");
        if (meridian) {
          const upper = meridian.toUpperCase();
          if (upper === "PM" && hours < 12) hours += 12;
          if (upper === "AM" && hours === 12) hours = 0;
        }
        const minutes = Number(minRaw ?? "0");
        const seconds = Number(secRaw ?? "0");
        return new Date(
          year,
          Number(mm) - 1,
          Number(dd),
          hours,
          minutes,
          seconds,
        );
      }

      return null;
    }

    if (typeof raw === "object") {
      if (typeof raw.toDate === "function") {
        const fromMethod = raw.toDate();
        return fromMethod instanceof Date ? fromMethod : null;
      }

      if (raw.$date != null) return parseTimestamp(raw.$date);

      if (raw.$numberLong != null)
        return parseTimestamp(Number(raw.$numberLong));

      if (raw.seconds != null) {
        const ms = raw.seconds * 1000 + (raw.nanoseconds || 0) / 1_000_000;
        return new Date(ms);
      }

      if (raw._seconds != null) {
        const ms = raw._seconds * 1000 + (raw._nanoseconds || 0) / 1_000_000;
        return new Date(ms);
      }
    }

    return null;
  };

  const deriveFromObjectId = (item) => {
    const source = item?._id || item?.id;
    if (typeof source !== "string" || source.length < 8) return null;
    const hex = source.slice(0, 8);
    if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
    const seconds = Number.parseInt(hex, 16);
    if (Number.isNaN(seconds)) return null;
    return new Date(seconds * 1000);
  };

  const filteredFeedback = useMemo(() => {
    if (!Array.isArray(feedbackData)) return [];

    return feedbackData
      .filter((item) => {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          !searchLower ||
          (item.title || item.subject || "")
            .toString()
            .toLowerCase()
            .includes(searchLower) ||
          (item.description || "")
            .toString()
            .toLowerCase()
            .includes(searchLower);

        const itemAcademyId = getItemAcademy(item);
        const matchesAcademy =
          !selectedAcademyId ||
          selectedAcademyId === "all" ||
          (itemAcademyId &&
            String(itemAcademyId) === String(selectedAcademyId));

        return matchesSearch && matchesAcademy;
      })
      .sort((a, b) => {
        const aTime = resolveSubmissionDate(a)?.getTime() ?? 0;
        const bTime = resolveSubmissionDate(b)?.getTime() ?? 0;
        return bTime - aTime;
      });
  }, [feedbackData, search, selectedAcademyId]);

  const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFeedback = filteredFeedback.slice(startIndex, endIndex);

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteFeedback(deleteConfirm._id).unwrap();
      toast.success("Feedback deleted successfully");
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Failed to delete feedback");
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 border-b pb-4">
        <h1 className="text-2xl font-semibold text-gray-900">
          Feedback & Reviews
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          {filteredFeedback.length} feedback
          {filteredFeedback.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search feedback..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <CustomSelect
          placeholder="All Academies"
          allOptionLabel="All Academies"
          value={selectedAcademyId}
          onChange={(value) => {
            setSelectedAcademyId(value);
            setCurrentPage(1);
          }}
          options={
            academies
              ? academies.map((academy) => ({
                  value: academy._id,
                  label: academy.name,
                }))
              : []
          }
        />
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Type</TableHead>
              <TableHead className="w-[200px]">Title</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[180px]">Academy</TableHead>
              <TableHead className="w-[180px]">Submitted</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFeedbackLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
                    <span className="text-sm text-gray-600">
                      Loading feedback...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : currentFeedback.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-600">No feedback found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentFeedback.map((item) => (
                <TableRow key={item._id || item.id}>
                  <TableCell>
                    {item.liked || item.reviewType === "positive" ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        Positive
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                        <ThumbsDown className="h-3 w-3 mr-1" />
                        Negative
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {item.title || item.subject || "No Title"}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs truncate">
                      {item.description || "No description"}
                    </div>
                  </TableCell>
                  <TableCell>{getAcademyDisplayName(item)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar className="h-3 w-3" />
                      {formatSubmissionTimestamp(item)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedFeedback(item)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(item)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {startIndex + 1} to{" "}
          {Math.min(endIndex, filteredFeedback.length)} of{" "}
          {filteredFeedback.length} results
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog
        open={!!selectedFeedback}
        onOpenChange={() => setSelectedFeedback(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedFeedback?.title || selectedFeedback?.subject}
            </DialogTitle>
            <DialogDescription>
              {selectedFeedback?.liked ||
              selectedFeedback?.reviewType === "positive" ? (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 mt-2">
                  <ThumbsUp className="h-3 w-3 mr-1" />
                  Positive Feedback
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100 mt-2">
                  <ThumbsDown className="h-3 w-3 mr-1" />
                  Negative Feedback
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Description</Label>
              <Textarea
                value={
                  selectedFeedback?.description || "No description provided"
                }
                readOnly
                className="mt-1.5 min-h-[100px]"
              />
            </div>
            <div>
              <Label>Academy</Label>
              <Input
                value={
                  selectedFeedback
                    ? getAcademyDisplayName(selectedFeedback)
                    : ""
                }
                readOnly
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Submitted</Label>
              <Input
                value={
                  selectedFeedback
                    ? formatSubmissionTimestamp(selectedFeedback)
                    : ""
                }
                readOnly
                className="mt-1.5"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Feedback</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feedback? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedbackReport;
